import {
  Box,
  Button,
  Checkbox,
  Code,
  Divider,
  Grid,
  Group,
  SegmentedControl,
  Table,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import {
  IconCopy,
  IconDeviceFloppy,
  IconExternalLink,
  IconFlask,
  IconInfoCircle,
  IconMail,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import Handlebars from 'handlebars';
import DailyLimitSettings from '../DailyLimitSettings';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import getMergeTags from '../../constants/getMergeTags';
import Regex from '../../constants/Regex';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import LockableButton from '../Lockable/LockableButton';
import ConfirmationModal from '../Modals/ConfirmationModal';
import MergeTagsModal from '../Modals/MergeTagsModal';
import SendTestMessageModal from '../Modals/SendTestMessageModal';
import ProductFeedSelector from '../ProductFeeds/ProductFeedSelector';
import DomainSecretSelect from '../Settings/DomainSecretSelect';
import Spinner from '../Spinner';
import WarningMessageOnDelete from '../WarningMessageOnDelete';

const validateHandlebarsValue = (value) => {
  if (!value) return null;
  try {
    Handlebars.parse(value);
    return null;
  } catch (e) {
    const firstLine = (e.message || '').split('\n')[0].trim();
    return `Invalid template: ${firstLine}`;
  }
};

const Mapper = ({ rows, setRows, setMergeTagModalOpen, showRequiredConfig, errors }) => {
  const setRow = (i, row) => {
    const rowsCopy = structuredClone(rows);
    rowsCopy[i] = row;
    setRows(rowsCopy);
  };

  const deleteRow = (i) => setRows(structuredClone(rows).filter((_, index) => i !== index));

  return (
    <>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Key</Table.Th>
            <Table.Th>Value</Table.Th>
            {showRequiredConfig && <Table.Th>Skip sending if blank?</Table.Th>}
            <Table.Th ta="right">
              {' '}
              <Button size="xs" variant="default" onClick={() => setRows([...rows, { key: '', value: '' }])}>
                <IconPlus size={14} /> Add Variable
              </Button>
              <Button size="xs" variant="default" ml="xs" onClick={() => setMergeTagModalOpen(true)}>
                <IconInfoCircle size={14} /> Merge Tags
              </Button>
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row, i) => {
            const handlebarsError = errors?.[i] ?? null;
            return (
              <Table.Tr key={i}>
                <Table.Td>
                  <TextInput
                    name="key"
                    size="xs"
                    type="text"
                    value={row.key}
                    placeholder="The property you wish to send to the webhook"
                    onChange={(e) => setRow(i, { ...row, [e.target.name]: e.target.value })}
                  />
                </Table.Td>
                <Table.Td>
                  <Textarea
                    name="value"
                    size="xs"
                    placeholder="The value you wish to send to the webhook for this property"
                    rows={1}
                    value={row.value}
                    onChange={(e) => setRow(i, { ...row, [e.target.name]: e.target.value })}
                    error={handlebarsError}
                  />
                </Table.Td>
                {showRequiredConfig && (
                  <Table.Td>
                    <Checkbox
                      name="skipOnBlank"
                      size="xs"
                      checked={row.skipOnBlank}
                      onChange={(e) => setRow(i, { ...row, [e.target.name]: e.target.checked })}
                    />
                  </Table.Td>
                )}
                <Table.Td style={{ textAlign: 'right' }}>
                  <Button size="xs" variant="default" onClick={() => deleteRow(i)}>
                    <IconTrash size={14} />
                  </Button>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </>
  );
};

const WebhookForm = ({ webhook: passedWebhook, saveWebhook, deleteWebhook, showUserFlow, deleteError, onBack }) => {
  const location = useLocation();
  const fetch = useFetch();
  const navigate = useNavigate();

  const [webhook, setWebhook] = useState();
  const [testWebhookOpen, setTestWebhookOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [mergeTagsModalOpen, setMergeTagsModalOpen] = useState(false);
  const [customFields, setCustomFields] = useState();
  const [coupons, setCoupons] = useState();
  const [surveys, setSurveys] = useState([]);
  const [loadError, setLoadError] = useState();
  const [userError, setUserError] = useState();

  const [sending, setSending] = useState(false);

  const { domain } = useSelector((state) => state.domain);

  useEffect(() => {
    setWebhook(passedWebhook);
  }, [passedWebhook]);

  useEffect(() => {
    const func = async () => {
      try {
        const { customFields } = await fetch(
          `/api/admin/accounts/${domain.accountId}/domains/${domain.id}/customFields`
        );
        await setCustomFields(customFields);
      } catch (e) {
        setLoadError(e);
      }
    };
    func();

    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/coupons`)
      .then(({ coupons }) => setCoupons(coupons))
      .catch(setLoadError);

    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/surveys`)
      .then(({ surveys }) => setSurveys(surveys))
      .catch(setLoadError);
  }, [domain.accountId, domain.id, fetch]);

  const send = (_, impersonateProfileQuery, triggerEventAction) => {
    setUserError(undefined);
    setSending(true);
    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/webhooks/testWebhook`, {
      method: 'POST',
      body: JSON.stringify({
        webhook,
        impersonateProfileQuery,
        triggerEventAction,
      }),
    })
      .then((response) => {
        if (response?.webhookSkipped) {
          setUserError({
            message:
              'Webhook was skipped because a required parameter (marked "Skip sending if blank?") compiled to a blank value for the impersonated profile.',
          });
          return;
        }
        setTestWebhookOpen(false);
      })
      .catch(setUserError)
      .finally(() => setSending(false));
  };

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (!customFields || !webhook) {
    return <Spinner />;
  }

  const isCustom = webhook.type === 'custom';
  const bodyMappingActive = isCustom && ['post', 'put'].includes(webhook.method);

  const queryMappingErrors = isCustom
    ? (webhook.queryMapping || []).map((row) => validateHandlebarsValue(row.value))
    : [];
  const bodyMappingErrors = bodyMappingActive
    ? (webhook.bodyMapping || []).map((row) => validateHandlebarsValue(row.value))
    : [];

  const hasHandlebarsErrors = queryMappingErrors.some(Boolean) || bodyMappingErrors.some(Boolean);

  const isInvalid =
    !webhook.name ||
    !webhook.url ||
    !webhook.contentType ||
    !webhook.type ||
    !webhook.url.match(Regex.urlRegex) ||
    hasHandlebarsErrors;

  const deleteButton = deleteWebhook ? (
    <LockableButton restrictionKey="webhooksDelete" color="red" onClick={() => setConfirmDeleteOpen(true)}>
      <IconTrash size={14} /> Delete
    </LockableButton>
  ) : undefined;

  let duplicateButton;
  if (webhook.id) {
    duplicateButton = (
      <LockableButton
        restrictionKey="webhooksCreate"
        color="cyan"
        onClick={() => {
          navigate(location.pathname.replace(`webhooks/${webhook.id}/details`, `webhooks/new?from=${webhook.id}`));
        }}
      >
        <IconCopy size={14} /> Make a copy
      </LockableButton>
    );
  }

  return (
    <>
      <MergeTagsModal
        show={mergeTagsModalOpen}
        mergeTags={getMergeTags(customFields, coupons, surveys, {
          includeProduct: Boolean(webhook?.productFeedId),
          includeFlowMessages: true,
        })}
        close={() => setMergeTagsModalOpen(false)}
      />

      <SendTestMessageModal
        send={send}
        close={() => setTestWebhookOpen(false)}
        error={userError}
        sending={sending}
        show={testWebhookOpen}
        showSendTo={false}
      />
      <Grid mb="md">
        <Grid.Col span={{ base: 12, sm: 2 }}>
          <Text size="sm" fw={500}>
            Name
          </Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 10 }}>
          <TextInput
            name="name"
            type="text"
            value={webhook.name}
            placeholder="e.g. Check This Out!"
            onChange={(e) => setWebhook({ ...webhook, [e.target.name]: e.target.value })}
          />
        </Grid.Col>
      </Grid>
      <Grid mb="md">
        <Grid.Col span={{ base: 12, sm: 2 }}>
          <Text size="sm" fw={500}>
            URL*
          </Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 8 }}>
          <TextInput
            name="url"
            type="text"
            value={webhook.url}
            placeholder="e.g. https://your-webhook.com/invoke/<uuid>"
            onChange={(e) => setWebhook({ ...webhook, [e.target.name]: e.target.value })}
            error={webhook.url && !webhook.url.match(Regex.urlRegex) ? 'Invalid URL' : undefined}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 2 }}>
          <Button variant="filled" fullWidth disabled={isInvalid} onClick={() => setTestWebhookOpen(true)}>
            <IconFlask size={14} style={{ marginRight: 4 }} /> Test Webhook
          </Button>
        </Grid.Col>
      </Grid>
      <Grid mb="md">
        <Grid.Col span={{ base: 12, sm: 2 }}>
          <Text size="sm" fw={500}>
            Type*
          </Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 10 }}>
          <SegmentedControl
            value={webhook.type ?? ''}
            onChange={(value) => setWebhook({ ...webhook, type: value })}
            data={[
              { label: 'Standard', value: 'standard' },
              { label: 'Custom', value: 'custom' },
            ]}
          />
          <Text size="sm" c="dimmed">
            Choose <strong>Standard</strong> if your webhook supports the{' '}
            <a
              target="_blank"
              rel="noreferrer"
              href="https://releva.ai/documentation/third-party-integration-apis/webhooks/"
            >
              Releva webhook speficiation <IconExternalLink size={14} />
            </a>
            <br />
            Otherwise, choose <strong>Custom</strong> and fill put the URL and Body Parameter Mapping below according to
            the webhook vendor's specification.
          </Text>
        </Grid.Col>
      </Grid>
      <Grid mb="md">
        <Grid.Col span={{ base: 12, sm: 2 }}>
          <Text size="sm" fw={500}>
            Product feed
          </Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 10 }}>
          <ProductFeedSelector
            accountId={domain.accountId}
            domainId={domain.id}
            forType="webook"
            onSelect={(productFeedId) => setWebhook({ ...webhook, productFeedId })}
            optional={true}
            productFeedId={webhook.productFeedId}
            scope="marketingChannels"
          />
        </Grid.Col>
      </Grid>
      <Grid mb="md">
        <Grid.Col span={{ base: 12, sm: 2 }}>
          <Text size="sm" fw={500}>
            Auth Secret
          </Text>
          <Text size="xs" c="dimmed">
            Optional. Leave unset to keep the webhook authenticating as it does now, or select a domain secret to use as
            the Authorization header value. Token-refresh secrets are automatically refreshed before each call.
          </Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 10 }}>
          <DomainSecretSelect
            accountId={domain.accountId}
            domainId={domain.id}
            placeholder="No secret selected"
            value={webhook.secretId}
            onChange={(secretId) => setWebhook({ ...webhook, secretId })}
            onError={setLoadError}
          />
        </Grid.Col>
      </Grid>
      {webhook.type === 'custom' && (
        <>
          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 2 }}>
              <Text size="sm" fw={500}>
                Request Method*
              </Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 10 }}>
              <SegmentedControl
                value={webhook.method ?? ''}
                onChange={(value) => setWebhook({ ...webhook, method: value })}
                data={[
                  { label: 'GET', value: 'get' },
                  { label: 'POST', value: 'post' },
                  { label: 'PUT', value: 'put' },
                  { label: 'DELETE', value: 'delete' },
                ]}
              />
            </Grid.Col>
          </Grid>
          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 2 }}>
              <Text size="sm" fw={500}>
                Format*
              </Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 10 }}>
              <SegmentedControl
                value={webhook.contentType ?? ''}
                onChange={(value) => setWebhook({ ...webhook, contentType: value })}
                data={[
                  { label: 'JSON', value: 'application/json' },
                  { label: 'URL-encoded form', value: 'application/x-www-form-urlencoded' },
                ]}
              />
            </Grid.Col>
          </Grid>
          <Divider my="md" />
          <Grid>
            <Grid.Col span={{ base: 12, sm: 2 }}>
              <Text size="sm" fw={500}>
                Request Headers
              </Text>
              <Text size="xs" c="dimmed">
                Only one Authorization header can reach the receiver, so avoid setting one here if this webhook also
                uses an Auth Secret.
              </Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 10 }}>
              <Mapper
                rows={webhook.headers}
                setRows={(headers) => setWebhook({ ...webhook, headers })}
                setMergeTagModalOpen={setMergeTagsModalOpen}
                showRequiredConfig={false}
              />
            </Grid.Col>
          </Grid>
          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 2 }}>
              <Text size="sm" fw={500}>
                Request URL Parameters*
              </Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 10 }}>
              <Mapper
                rows={webhook.queryMapping}
                setRows={(queryMapping) => setWebhook({ ...webhook, queryMapping })}
                setMergeTagModalOpen={setMergeTagsModalOpen}
                showRequiredConfig={true}
                errors={queryMappingErrors}
              />
            </Grid.Col>
          </Grid>
          {['post', 'put'].includes(webhook.method) && (
            <>
              <Divider my="md" />
              <Grid mb="md">
                <Grid.Col span={{ base: 12, sm: 2 }}>
                  <Text size="sm" fw={500}>
                    Request Body Parameters*
                  </Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 10 }}>
                  <Mapper
                    rows={webhook.bodyMapping}
                    setRows={(bodyMapping) => setWebhook({ ...webhook, bodyMapping })}
                    setMergeTagModalOpen={setMergeTagsModalOpen}
                    showRequiredConfig={true}
                    errors={bodyMappingErrors}
                  />
                </Grid.Col>
              </Grid>
            </>
          )}
          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 10 }} offset={{ sm: 2 }}>
              <Text size="sm" c="dimmed">
                The <Code>{'{{flowMessage.viberMessage.*}}'}</Code> and <Code>{'{{flowMessage.textMessage.*}}'}</Code>{' '}
                merge tags carry the exact Viber or SMS message that the campaign triggering this webhook already sent
                to the profile, including its tracking links and any coupon code it used. Email, push and WhatsApp
                don&apos;t expose these tags yet. They stay empty when the campaign sent that profile nothing, and in
                Test Webhook, because there is no campaign behind it. A required parameter mapped to one, with
                &quot;Skip sending if blank?&quot; ticked, will always report the test as skipped.
              </Text>
            </Grid.Col>
          </Grid>
        </>
      )}

      <DailyLimitSettings
        model={webhook}
        onChange={(key, value) => setWebhook({ ...webhook, [key]: value })}
        channelLabel="webhooks"
        maxPerDay={domain.maxWebhooksPerDay}
        accountId={domain.accountId}
        domainId={domain.id}
        settingsTab="webhooks"
      />

      <Group justify="flex-end" gap="xs" py="md">
        <Button variant="default" onClick={onBack}>
          {' '}
          Back
        </Button>
        {deleteButton}
        {duplicateButton}
        {showUserFlow && (
          <Button variant="default" onClick={() => showUserFlow()}>
            <IconMail size={14} /> Send
          </Button>
        )}
        <LockableButton
          restrictionKey={`webhooks${webhook.id ? 'Update' : 'Create'}`}
          variant="primary"
          onClick={() => saveWebhook(webhook)}
          disabled={isInvalid}
        >
          <IconDeviceFloppy size={14} /> Save
        </LockableButton>
      </Group>
      <ConfirmationModal
        show={deleteError ? false : confirmDeleteOpen}
        close={() => setConfirmDeleteOpen(false)}
        confirm={(cb) => deleteWebhook(cb)}
        message={<WarningMessageOnDelete type="Text Message" />}
      />
      {deleteError ? (
        <Box ta="right">
          <ErrorMessage message={deleteError} />
        </Box>
      ) : null}
    </>
  );
};

export default WebhookForm;
